// Production-grade Bingo app screen.
// Zero Tailwind — all colours, radii, shadows, and fonts come from theme.js.
// Styles are applied via inline style objects referencing theme tokens, or via
// CSS class names whose rules are injected by injectTheme().
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import {
  Zap,
  Play,
  Wallet,
  History,
  User,
  BookOpen,
  TrendingUp,
  Users,
  Gamepad2,
  ChevronRight,
  Coins,
} from "lucide-react";
import { theme, injectTheme } from "../assets/theme";
import StakeSelectionModal from "./modals/StakeSelectionModal";
import CartelaSelectionModal from "./modals/CartelaSelectionModal";
import { useAuth } from "../contexts/AuthContext";
import { useGame } from "../contexts/GameContext";

// ─── Data ────────────────────────────────────────────────────────────────────
import mainLogo from "../assets/logo/bingo.png";
const RECENT_WINNERS = [
  { id: 1, name: "Alex_Glow", bet: "10 Bir", prize: "1,280 ETB", rank: 1 },
  { id: 2, name: "BingoQueen", bet: "25 Bir", prize: "3,400 ETB", rank: 2 },
  { id: 3, name: "NeonRider", bet: "5 Bir", prize: "800 ETB", rank: 3 },
  { id: 4, name: "LuckyStrike", bet: "15 Bir", prize: "2,100 ETB", rank: 4 },
];

const STATS = [
  { label: "Active Players", value: "14,208", Icon: Users },
  { label: "Total Games", value: "1.2M+", Icon: Gamepad2 },
  { label: "Daily Payout", value: "45.8K", Icon: TrendingUp },
];

// ─── Rank dot colours ────────────────────────────────────────────────────────

function rankStyle(rank) {
  const map = {
    1: { background: theme.color.rankGold, boxShadow: theme.shadow.rankGold },
    2: { background: theme.color.rankSilver, boxShadow: theme.shadow.rankSilv },
    3: { background: theme.color.rankBronze, boxShadow: theme.shadow.rankBrnz },
  };
  return map[rank] ?? { background: theme.color.rankDefault };
}

// ─── CoinBadge ───────────────────────────────────────────────────────────────

function CoinBadge() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "20px",
        height: "20px",
        borderRadius: theme.radius.full,
        background: theme.color.accentMuted,
        border: `1px solid ${theme.color.borderAccent}`,
        flexShrink: 0,
      }}
    >
      <Coins size={11} color={theme.color.accentLight} />
    </span>
  );
}

// ─── WinnerRow ───────────────────────────────────────────────────────────────

function WinnerRow({ winner }) {
  return (
    <div
      className="winner-row fade-in-up"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        borderRadius: theme.radius.lg,
        background: theme.color.bgCard,
        border: `1px solid ${theme.color.borderCard}`,
        animationDelay: `${(winner.id - 1) * 0.07}s`,
      }}
    >
      {/* Left: rank dot + name */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span
          style={{
            display: "inline-block",
            width: "8px",
            height: "8px",
            borderRadius: theme.radius.full,
            flexShrink: 0,
            ...rankStyle(winner.rank),
          }}
        />
        <span
          style={{
            fontSize: "13px",
            fontWeight: theme.font.weightSemi,
            color: theme.color.textSub,
            fontFamily: theme.font.bodyFamily,
            letterSpacing: "0.02em",
          }}
        >
          {winner.name}
        </span>
      </div>

      {/* Center: bet pill */}
      <span
        style={{
          fontSize: "11px",
          fontWeight: theme.font.weightMed,
          color: theme.color.textFaint,
          background: theme.color.bgBetPill,
          border: `1px solid ${theme.color.borderDivide}`,
          borderRadius: theme.radius.full,
          padding: "2px 10px",
          fontFamily: theme.font.bodyFamily,
        }}
      >
        {winner.bet}
      </span>

      {/* Right: prize + coin */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span
          style={{
            fontSize: "13px",
            fontWeight: theme.font.weightBold,
            color: theme.color.textAccent,
            fontVariantNumeric: "tabular-nums",
            fontFamily: theme.font.bodyFamily,
          }}
        >
          {winner.prize}
        </span>
        <CoinBadge />
      </div>
    </div>
  );
}

// ─── PlayButton ──────────────────────────────────────────────────────────────

function PlayButton({ onClick }) {
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "220px",
        height: "220px",
      }}
    >
      {/* Glow ring 1 */}
      <span
        className="ping-ring-1"
        style={{
          position: "absolute",
          width: "200px",
          height: "200px",
          borderRadius: theme.radius.full,
          background: theme.color.accentMuted,
          pointerEvents: "none",
        }}
      />
      {/* Glow ring 2 */}
      <span
        className="ping-ring-2"
        style={{
          position: "absolute",
          width: "170px",
          height: "170px",
          borderRadius: theme.radius.full,
          background: "rgba(249,115,22,0.08)",
          pointerEvents: "none",
        }}
      />

      {/* Main button */}
      <button
        className="play-btn"
        onClick={onClick}
        aria-label="Play Bingo"
        style={{
          position: "relative",
          zIndex: theme.z.above,
          width: "140px",
          height: "140px",
          borderRadius: theme.radius.full,
          background: `radial-gradient(circle at 35% 35%, ${theme.color.playFrom}, ${theme.color.playVia} 55%, ${theme.color.playTo})`,
          boxShadow: theme.shadow.playBtn,
          border: "none",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
        }}
      >
        <Play
          size={38}
          color="#fff"
          fill="#fff"
          style={{
            marginLeft: "4px",
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
          }}
        />
        <span
          style={{
            color: "#fff",
            fontFamily: theme.font.family,
            fontWeight: theme.font.weightBlack,
            fontSize: "15px",
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            textShadow: "0 1px 4px rgba(0,0,0,0.4)",
          }}
        >
          PLAY
        </span>
      </button>

      {/* Coin badge on button edge */}
      {/* <span
        style={{
          position: "absolute",
          bottom: "36px",
          right: "32px",
          zIndex: theme.z.top,
          width: "28px",
          height: "28px",
          borderRadius: theme.radius.full,
          background: theme.color.coinBg,
          border: `2px solid ${theme.color.bgSurface}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: theme.shadow.coin,
        }}
      >
        <Coins size={14} color={theme.color.coinFg} />
      </span> */}
    </div>
  );
}

// ─── StatCard ────────────────────────────────────────────────────────────────

function StatCard({ stat }) {
  const { Icon } = stat;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "5px",
        padding: "0 14px",
        flex: 1,
      }}
    >
      <Icon
        size={15}
        color={theme.color.accentLight}
        style={{ opacity: 0.75 }}
      />
      <span
        style={{
          fontSize: "17px",
          fontWeight: theme.font.weightBlack,
          color: theme.color.textPrimary,
          fontFamily: theme.font.family,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
        }}
      >
        {stat.value}
      </span>
      <span
        style={{
          fontSize: "9px",
          fontWeight: theme.font.weightMed,
          color: theme.color.textDim,
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          textAlign: "center",
          lineHeight: 1.2,
          fontFamily: theme.font.bodyFamily,
        }}
      >
        {stat.label}
      </span>
    </div>
  );
}

// ───  (Main Screen) ─────────────────────────────────────────────────

export default function PlayHomeComp({ onGameStart }) {
  const { balance, user } = useAuth();
  const {
    showStakeModal,
    showCartelaModal,
    availableCartelas,
    openStakeModal,
    selectStake,
    setShowStakeModal,
    setShowCartelaModal,
    activeRoom,
    loading,
    joinError,
    takenCartelaIds,
    myCartelaIds,
    lobbyPlayerCount,
    lobbyEndsAt,
    selectCartela,
    deselectCartela,
    leaveCartelaSelection,
    estimatedPot,
  } = useGame();
  const handlePlay = () => openStakeModal(); // was setshowStakeSelectionModal(true)

  // Inject CSS variables + Google Fonts on mount
  useEffect(() => {
    injectTheme();
  }, []);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        background: theme.color.bgSurface,
        overflow: "hidden",
        boxShadow: theme.shadow.phone,
        display: "flex",
        flexDirection: "column",
        minHeight: "780px",
        height: "100vh",
      }}
    >
      {/* ── Scrollable content ──────────────────────────── */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "0 16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          scrollbarWidth: "none",
          paddingBottom: "100px",
        }}
      >
        {/* ── Header ─────────────────────────────────────── */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "34px",
                height: "34px",
                borderRadius: theme.radius.md,
                background: `linear-gradient(135deg, ${theme.color.playFrom}, ${theme.color.playTo})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: theme.shadow.logo,
              }}
            >
              <img
                src={mainLogo}
                alt={"Kazna bingo"}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
            <span
              style={{
                fontFamily: theme.font.family,
                fontWeight: theme.font.weightBlack,
                fontSize: "20px",
                letterSpacing: "-0.01em",
                color: theme.color.textPrimary,
              }}
            >
              kazna
              <span style={{ color: theme.color.accentLight }}> Bingo</span>
            </span>
          </div>
        </header>

        {/* ── Greeting ───────────────────────────────────── */}
        <div style={{ padding: "6px 24px 2px" }}>
          <p
            style={{
              fontSize: "10px",
              fontFamily: theme.font.bodyFamily,
              fontWeight: theme.font.weightMed,
              color: theme.color.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.18em",
              marginBottom: "2px",
            }}
          >
            Welcome back,
          </p>
          <h1
            style={{
              fontFamily: theme.font.family,
              fontWeight: theme.font.weightBlack,
              fontSize: "28px",
              color: theme.color.textPrimary,
              lineHeight: 1,
              letterSpacing: "-0.01em",
            }}
          >
            {user.username}
          </h1>
        </div>

        {/* ── Hero: Play button ───────────────────────────── */}
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px 0 20px",
            gap: "16px",
          }}
        >
          <PlayButton onClick={handlePlay} />
          {joinError && <p style={{ color: "#ef4444" }}>{joinError}</p>}

          <p
            style={{
              fontSize: "11px",
              fontFamily: theme.font.bodyFamily,
              fontWeight: theme.font.weightMed,
              color: theme.color.textFaint,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
            }}
          >
            Ready for your next bingo?
          </p>
        </section>

        {/* ── Divider ────────────────────────────────────── */}
        <div
          style={{
            margin: "0 24px",
            height: "1px",
            background: `linear-gradient(to right, transparent, ${theme.color.borderDivide}, transparent)`,
          }}
        />

        {/* ── Recent Winners ─────────────────────────────── */}
        <section style={{ padding: "18px 24px", flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "14px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  width: "4px",
                  height: "20px",
                  borderRadius: theme.radius.full,
                  background: theme.color.accent,
                  boxShadow: theme.shadow.glowBar,
                  flexShrink: 0,
                }}
              />
              <h2
                style={{
                  fontFamily: theme.font.family,
                  fontWeight: theme.font.weightBlack,
                  fontSize: "13px",
                  color: theme.color.textPrimary,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Recent Winners
              </h2>
            </div>

            <button
              className="see-all-btn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "2px",
                fontSize: "11px",
                fontFamily: theme.font.bodyFamily,
                fontWeight: theme.font.weightSemi,
                color: theme.color.textAccentDim,
              }}
            >
              See all <ChevronRight size={12} />
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {RECENT_WINNERS.map((w) => (
              <WinnerRow key={w.id} winner={w} />
            ))}
          </div>
        </section>

        {/* ── Stats bar ──────────────────────────────────── */}
        <section
          style={{
            margin: "0 16px 12px",
            borderRadius: theme.radius.xl,
            background: theme.color.bgStats,
            border: `1px solid ${theme.color.border}`,
            padding: "16px 8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {STATS.map((s, i) => (
              <div key={s.label} style={{ display: "contents" }}>
                <StatCard stat={s} />
                {i < STATS.length - 1 && (
                  <span
                    style={{
                      width: "1px",
                      height: "36px",
                      background: theme.color.borderDivide,
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      </div>

      <StakeSelectionModal
        open={showStakeModal}
        onClose={() => setShowStakeModal(false)}
        onConfirm={selectStake} // GameContext.selectStake(stakeId)
        walletBalance={balance.toFixed(2)}
      />

      <CartelaSelectionModal
        open={showCartelaModal}
        wallet={balance.toFixed(2)}
        stake={activeRoom ? String(activeRoom.stake) : "0"}
        potSize={activeRoom?.pot_size ?? "0"} // was activeRoom?.pot — that field doesn't exist in your schema
        cartelas={availableCartelas}
        takenCartelaIds={takenCartelaIds}
        myCartelaIds={myCartelaIds}
        estimatedPot={estimatedPot}
        playerCount={lobbyPlayerCount}
        endsAt={lobbyEndsAt}
        onSelect={selectCartela}
        onDeselect={deselectCartela}
        onBack={leaveCartelaSelection}
        onClose={leaveCartelaSelection}
      />
    </div>
  );
}
